'use client';

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { borderNeutral300 } from '@osmosis/tailwind-ui/styles/border';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import {
  Citation,
  Tag as TagProps,
  BreadcrumbV2Object,
} from '@osmosis/shared/types/learn';
import { Thumbnail, Updates } from '@osmosis/shared/types/learndb/learnData';
import { useVideoThumbnail } from '@osmosis/nextjs-components/hooks/useVideoThumbnail';

import useModal from '../../../hooks/useModal';
import { useUserContext } from '../../../context/UserContext';
import LearnShareButton from '../../LearnShareButton';
import AddToPlaylistButton from '../../../app/_components/playlist/AddToPlaylistButton';
import { TBRLabel } from '../../TBRLabel';
import WatchedPill from '../WatchedPill';
import useWatchedConfirmation from '../../../hooks/useWatchedConfirmation';
import { faBookmark } from '@fortawesome/pro-regular-svg-icons/faBookmark';
import { useContentSize } from '@osmosis/nextjs-components/context/ContentSizeContext';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { bg0 } from '@osmosis/tailwind-ui/styles';
import LearnBreadcrumbs from './LearnBreadcrumbs';

const FeedbackForm = dynamic(() => import('../FeedbackForm'), { ssr: false });
const VideoInformation = dynamic(() => import('../VideoInformation'), {
  ssr: false,
});

interface Props {
  title: string;
  learnNodeID: number;
  mediaID?: number;
  views: number;
  citations: Citation;
  modified: string;
  learnPageCreationDate?: string;
  lastUpdated?: string;
  lastReviewed?: string;
  updates?: Updates[];
  thumbnails?: Thumbnail;
  url?: string;
  tags?: TagProps[];
  infoModalIsVideoPage?: boolean;
  breadcrumbsv2?: BreadcrumbV2Object[];
}

const COMMON_BUTTON_CLASS = `p-2 text-sm font-bold hover:bg-blue-100 border-0 hover:text-darkest-blue-500 ${text5} ${bg0}`;

/**
 * V2 Learn page header.
 */
const LearnHeader = (props: Props): JSX.Element => {
  const {
    title,
    learnNodeID,
    mediaID,
    views = 0,
    citations,
    modified,
    learnPageCreationDate,
    lastUpdated,
    lastReviewed,
    updates,
    thumbnails,
    url,
    tags = [],
    infoModalIsVideoPage,
    breadcrumbsv2,
  } = props;

  const intl = useIntl();
  const { user } = useUserContext();
  const thumbnail = useVideoThumbnail(thumbnails);
  const { confirmed: isWatched, shouldAnnounce } = useWatchedConfirmation({
    mediaID,
  });

  const videoInfoModal = useModal();
  const feedbackModal = useModal();

  // Breadcrumb trail starts collapsed; the chevron beside the title toggles it.
  const [breadcrumbsExpanded, setBreadcrumbsExpanded] = useState(false);
  const hasBreadcrumbs = !!breadcrumbsv2?.length;

  const { md, getClassValue } = useContentSize();

  const HEADER_TOOLTIP_ID = 'v2-header-tooltip';

  const infoIconButtonClass = getClassValue({
    defaultClasses: 'p-1 md:p-2',
    breakpoints: {
      md: 'p-2',
      base: 'p-1',
    },
  });

  const containerClass = getClassValue({
    defaultClasses: 'px-3 py-2 md:px-4',
    breakpoints: { md: 'px-4 py-1', base: 'px-3 py-2' },
  });

  // The breadcrumb band is desktop-only and must use the SAME container-based
  // breakpoint as the breadcrumb content (and the rest of the header) — a
  // viewport `md:` would let the band show as an empty bordered strip when the
  // AI panel shrinks the content column below md on a wide viewport.
  const breadcrumbBandClass = getClassValue({
    defaultClasses: 'hidden px-3 md:block md:px-4',
    breakpoints: { md: 'block px-4', base: 'hidden' },
  });

  // The breadcrumb toggle follows the band: hidden on mobile (where breadcrumbs
  // aren't shown), so the chevron isn't a dead control.
  const breadcrumbToggleClass = getClassValue({
    defaultClasses: 'hidden md:flex',
    breakpoints: { md: 'flex', base: 'hidden' },
  });

  const shareLabel = intl.formatMessage({
    id: 'share-button-share',
    defaultMessage: 'Share',
  });

  const isVideoPage = infoModalIsVideoPage;

  return (
    <>
      <Box className="relative">
        <Flex
          className={cn(
            'w-full flex-nowrap items-center justify-between gap-2 min-h-[46px]',
            isVideoPage ? 'border-b' : '',
            borderNeutral300,
            containerClass,
            isWatched && 'pt-5',
          )}
        >
          <Flex className={`min-w-0 flex-1 items-center gap-2`}>
            <H1
              data-testid="learn-header-title"
              data-schema="learn-speakable-title"
              className={`min-w-0 truncate !text-lg !font-bold`}
            >
              {title}
            </H1>
            <WatchedPill
              confirmed={isWatched}
              shouldAnnounce={shouldAnnounce}
            />
            <TBRLabel tags={tags} />
            {hasBreadcrumbs && (
              <Button
                variant="transparent"
                onClick={() => setBreadcrumbsExpanded((prev) => !prev)}
                data-testid="learn-breadcrumbs-toggle"
                aria-expanded={breadcrumbsExpanded}
                aria-label={intl.formatMessage({
                  id: 'learn.breadcrumbs.toggle',
                  defaultMessage: 'Toggle breadcrumbs',
                })}
                className={cn(
                  `${text5} !px-2 text-sm font-bold hover:bg-blue-100 hover:text-darkest-blue-500`,
                  breadcrumbToggleClass,
                )}
              >
                <Icon
                  icon={breadcrumbsExpanded ? faChevronUp : faChevronDown}
                  size={14}
                />
              </Button>
            )}
          </Flex>

          <Flex className={cn('shrink-0 items-center gap-1')}>
            <Flex className={`items-center justify-center flex-nowrap gap-1`}>
              <Button
                variant="transparent"
                startIcon={<Icon icon={faInfoCircle} size={16} />}
                onClick={videoInfoModal.toggle}
                data-testid="learn-header-info-button"
                data-tooltip-id={HEADER_TOOLTIP_ID}
                data-tooltip-content={title}
                className={cn(
                  text5,
                  'flex items-center justify-center [&>div:first-child]:self-start [&>div:first-child]:mr-0',
                  COMMON_BUTTON_CLASS,
                  infoIconButtonClass,
                )}
              />
            </Flex>

            <Box
              data-tooltip-id={HEADER_TOOLTIP_ID}
              data-tooltip-content={shareLabel}
            >
              <LearnShareButton
                thumbnail={thumbnail}
                url={url}
                concept={title}
                label={md ? shareLabel : undefined}
                iconButtonProps={{
                  iconSize: 16,
                  className: COMMON_BUTTON_CLASS,
                }}
              />
            </Box>
            <Box>
              <AddToPlaylistButton
                data-testid="learn-header-add-to-playlist-button"
                isIconButton={!md}
                items={[{ name: title, learnNodeID }]}
                iconButtonProps={{
                  icon: <Icon icon={faBookmark} size={16} />,
                  className: COMMON_BUTTON_CLASS,
                }}
                buttonProps={{
                  variant: 'transparent',
                  color: 'default',
                  className: COMMON_BUTTON_CLASS,
                }}
              />
            </Box>
          </Flex>
        </Flex>

        {hasBreadcrumbs ? (
          <Box
            data-testid="learn-breadcrumbs-band"
            className={cn(
              'py-2',
              bg0,
              borderNeutral300,
              breadcrumbBandClass,
              breadcrumbsExpanded ? '' : '!hidden',
              isVideoPage ? 'absolute inset-x-0 top-full z-20 border-b ' : '',
            )}
          >
            <LearnBreadcrumbs
              breadcrumbsv2={breadcrumbsv2}
              currentTitle={title}
            />
          </Box>
        ) : null}
      </Box>

      <Tooltip id={HEADER_TOOLTIP_ID} place="bottom" />

      {videoInfoModal.isVisible && (
        <VideoInformation
          {...videoInfoModal}
          loggedIn={!!user}
          title={title}
          citations={citations}
          learnPageCreationDate={learnPageCreationDate}
          modified={modified}
          lastUpdated={lastUpdated}
          lastReviewed={lastReviewed}
          views={views}
          showViews
          isVideoPage={infoModalIsVideoPage}
          updates={updates}
          toggleFeedbackModal={feedbackModal.toggle}
          isFeedbackModalVisible={feedbackModal.isVisible}
          data-testid="learn-header-video-info"
        />
      )}
      {feedbackModal.isVisible && (
        <FeedbackForm
          {...feedbackModal}
          videoTitle={title}
          nodeID={learnNodeID}
          subjectText={intl.formatMessage(
            {
              id: 'learn.header.feedback_subject_video',
              defaultMessage: 'Subject: Feedback on video -- {videoTitle}',
            },
            { videoTitle: title },
          )}
        />
      )}
    </>
  );
};

export default LearnHeader;
